// Initialize moment globally BEFORE any other imports
// This is required for moment-timezone to work correctly with Vite
import './polyfills/moment-init';

// Setup chunk loading error handler for better resilience
import { setupChunkErrorHandler } from './polyfills/chunk-retry';

import React from 'react';
import ReactDOM from 'react-dom';

import './styles/main.scss';

import i18n, { aliases } from './i18n/i18n';
import { BaseAPIModel, fetcher } from '@weezevent/weezjs-core';
import { PermissionsLayer } from './user/permissions';

import App from './app';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { ToastProvider, MixPanelProvider } from '@weezevent/nacre';

import AppContextProvider from './context/appContextProvider';
import Loading from './components/loading';

import { useAuth } from 'react-oidc-context';
import { Authentication } from './user/authentication';
import ErrorBoundary from './components/errorBoundary';
import TrackingProvider from './components/tracking/trackingProvider';
import NotificationsProvider from './components/notifications/notificationsProvider';

import '@weezevent/smart-table/dist/index.css';

setupChunkErrorHandler();

// Lazy initialize Sentry and Datadog - they're only needed after initial render
let Sentry = null;
let datadogRum = null;

// Dynamically import monitoring tools
const initSentry = async () => {
    if (!Sentry && window.weezConfig.sentry) {
        const module = await import('@sentry/browser');
        Sentry = module;
        return module;
    }
    return Sentry;
};

const initDatadog = async () => {
    if (!datadogRum && window.weezConfig.datadog) {
        const module = await import('@datadog/browser-rum');
        datadogRum = module.datadogRum;
        return datadogRum;
    }
    return datadogRum;
};

// Developpers should not add sentry to localconfig to prevent useless errors
// Initialize Sentry asynchronously to avoid blocking initial render
if (window.weezConfig.sentry) {
    initSentry().then(SentryModule => {
        if (SentryModule) {
            SentryModule.init({
                dsn: window.weezConfig.sentry.dsn,
                beforeSend: (event, hint) => {
                    if (hint.originalException?.code === 403) {
                        return null;
                    }
                    return event;
                },
                ignoreErrors: [
                    // User is offline
                    'NetworkError',
                    'Network Error',
                    'Fetch Error',
                    'Failed to fetch',
                    'Load failed',
                    'Error requesting resource',
                    // ResizeObserver triggered too many updates, some were ignored
                    'ResizeObserver loop completed with undelivered notifications.'
                ],
                environment: window.location.host,
                release: 'willy-' + (window.weezConfig.version || 'dev')
            });
            SentryModule.addEventProcessor(function(event) {
                // Adds a route_path tag to help identify which routes have trouble.
                event.tags ||= {};
                event.tags.route_path = window.location.pathname.replace(/\/([OE][0-9]+|[a-z0-9-]{36})\b/gi, '/[id]').slice(0, 200);
                return event;
            });
        }
    });
}

// Initialize Datadog asynchronously to avoid blocking initial render
if (window.weezConfig.datadog) {
    initDatadog().then(ddRum => {
        if (ddRum) {
            ddRum.init({
                applicationId: window.weezConfig.datadog.applicationId,
                clientToken: window.weezConfig.datadog.clientToken,
                env: window.weezConfig.datadog.env,
                site: window.weezConfig.datadog.site,
                service: window.weezConfig.datadog.service,
                sampleRate: window.weezConfig.datadog.sampleRate,
                sessionReplaySampleRate: window.weezConfig.datadog.sessionReplaySampleRate,
                trackInteractions: window.weezConfig.datadog.trackInteractions,
                trackResources: window.weezConfig.datadog.trackResources,
                trackLongTasks: window.weezConfig.datadog.trackLongTasks,
                defaultPrivacyLevel: window.weezConfig.datadog.defaultPrivacyLevel,
                version: window.weezConfig.version,
                enableExperimentalFeatures: ['clickmap']
            });
            ddRum.startSessionReplayRecording();
        }
    });
}

try {
    // Out useApiModel hook fails silently by default.
    // This is a very bad idea. But we can't change it
    // in one go. You can tell useApiModel to fail on server
    // error by  running this in the console on your machine :
    // localStorage.enableBaseAPIModelStrictMode='yes'
    // and reloading the page.
    if (localStorage.enableBaseAPIModelStrictMode) {
        // Once strict mode is enabled, the useApiModel hook will
        // no longer fail silently and will instead crash the page
        // when some of the data could not be loaded, and offer the
        // user and chance to reload the page.
        BaseAPIModel.strictByDefault = true;
        // After a while, we can toggle this on for everybody.
        // This two steps approach should help us catch pages
        // where we're currenly expecting api calls to fail.
    }
} catch (e) {
    console.warn(e);
}

function Initialize({ children }) {
    // Redundant until we can remove internal fetcher
    const apiUrl = window.weezConfig.api.apiUrl;
    fetcher.initialize(apiUrl);

    const auth = useAuth();

    // Write the global mutable state
    const tokenType = 'Bearer'; //Should be auth.user.token_type
    fetcher.setAuthorizationHeader(`${tokenType} ${auth.user.access_token}`);

    // fetcher.setToken(auth.user.token);

    const userLocale = auth.user.profile.locale || auth.user.profile.data?.locale || 'en';
    const accountsTokenLocale = userLocale.replace(/_/g, '-');
    const locale = aliases[accountsTokenLocale] ?? accountsTokenLocale;
    i18n.accountLocale = accountsTokenLocale;
    if (locale !== i18n.language) {
        i18n.changeLanguage(locale);
        fetcher.setLanguage(locale);
    }

    return children;
}

// Wrapper to conditionally render MixPanelProvider only if token exists
function ConditionalMixPanelProvider({ token, children }) {
    if (token) {
        return <MixPanelProvider token={token}>{children}</MixPanelProvider>;
    }
    return <>{children}</>;
}

export default function Willy({ children }) {
    return (
        <React.Suspense fallback={<Loading />}>
            <ToastProvider>
                <ConditionalMixPanelProvider token={window.weezConfig?.mixpanel}>
                    <Router>
                        <Authentication config={window.weezConfig.connect}>
                            <Initialize>
                                <PermissionsLayer>
                                    <TrackingProvider token={window.weezConfig?.segment}>
                                        <NotificationsProvider>
                                            <AppContextProvider>
                                                <div className="sauron">
                                                    <div className="app-route">
                                                        <Route path="/">
                                                            <ErrorBoundary location={window.location}>
                                                                <App />
                                                            </ErrorBoundary>
                                                        </Route>
                                                    </div>
                                                </div>
                                            </AppContextProvider>
                                        </NotificationsProvider>
                                    </TrackingProvider>
                                </PermissionsLayer>
                            </Initialize>
                        </Authentication>
                    </Router>
                </ConditionalMixPanelProvider>
            </ToastProvider>
        </React.Suspense>
    );
}

ReactDOM.render(<Willy />, document.getElementById('main'));
